import { ChangeEvent } from "react";
import { Dayjs } from "dayjs";
import "dayjs/locale/th";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import Grid from "@mui/material/Grid";
import InputAdornment from "@mui/material/InputAdornment";
import MenuItem from "@mui/material/MenuItem";
import TextField from "@mui/material/TextField";
import SearchIcon from "@mui/icons-material/Search";
import { Branch } from "../../types/user.type";
import {
  CreditDebitNoteFilterParams,
  NoteReasonType,
  NoteSettlementMethod,
  NoteStatus,
  NoteType,
} from "../../types/credit-debit-note.type";
import { formatDateParam } from "../ETax/dateRangeParams";
import {
  ALL,
  NOTE_REASON_TYPE_FILTER,
  NOTE_SEARCH_MAX_LENGTH,
  NOTE_SETTLEMENT_FILTER,
  NOTE_STATUS_FILTER,
  NOTE_TYPE_FILTER,
} from "./constants";

export interface NoteListFilterValue {
  /** ALL, or the branch_code head office / master picked. */
  branchCode: string;
  noteType: NoteType | typeof ALL;
  dateFrom: Dayjs | null;
  dateTo: Dayjs | null;
  status: NoteStatus | typeof ALL;
  settlementMethod: NoteSettlementMethod | typeof ALL;
  reasonType: NoteReasonType | typeof ALL;
  search: string;
}

export const EMPTY_NOTE_LIST_FILTER: NoteListFilterValue = {
  branchCode: ALL,
  noteType: ALL,
  dateFrom: null,
  dateTo: null,
  status: ALL,
  settlementMethod: ALL,
  reasonType: ALL,
  search: "",
};

/** A picked filter value, or nothing for ทั้งหมด. */
const pickedValue = <Value extends string>(value: Value | typeof ALL): Value | undefined =>
  value === ALL ? undefined : value;

/** The query the list and its export send for a filter: ทั้งหมด, blank text and unfinished dates are left out. */
export const noteFilterParams = (filter: NoteListFilterValue): CreditDebitNoteFilterParams => ({
  note_type: pickedValue(filter.noteType),
  date_from: formatDateParam(filter.dateFrom) ?? undefined,
  date_to: formatDateParam(filter.dateTo) ?? undefined,
  status: pickedValue(filter.status),
  settlement_method: pickedValue(filter.settlementMethod),
  reason_type: pickedValue(filter.reasonType),
  search: filter.search.trim() || undefined,
  branch_code: pickedValue(filter.branchCode),
});

interface NoteListFilterProps {
  canSelectBranch: boolean;
  branches: Branch[];
  value: NoteListFilterValue;
  onChange: (patch: Partial<NoteListFilterValue>) => void;
}

const searchSlotProps = {
  htmlInput: { maxLength: NOTE_SEARCH_MAX_LENGTH },
  input: {
    startAdornment: (
      <InputAdornment position="start">
        <SearchIcon fontSize="small" />
      </InputAdornment>
    ),
  },
};

export default function NoteListFilter({
  canSelectBranch,
  branches,
  value,
  onChange,
}: Readonly<NoteListFilterProps>) {
  const handleBranchChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ branchCode: event.target.value });
  const handleNoteTypeChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({
      noteType:
        NOTE_TYPE_FILTER.find((option) => option.value === event.target.value)?.value ?? ALL,
    });
  const handleStatusChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({
      status:
        NOTE_STATUS_FILTER.find((option) => option.value === event.target.value)?.value ?? ALL,
    });
  const handleSettlementChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({
      settlementMethod:
        NOTE_SETTLEMENT_FILTER.find((option) => option.value === event.target.value)?.value ?? ALL,
    });
  const handleReasonTypeChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({
      reasonType:
        NOTE_REASON_TYPE_FILTER.find((option) => option.value === event.target.value)?.value ??
        ALL,
    });
  const handleSearchChange = (event: ChangeEvent<HTMLInputElement>) =>
    onChange({ search: event.target.value });
  const handleDateFromChange = (date: Dayjs | null) => onChange({ dateFrom: date });
  const handleDateToChange = (date: Dayjs | null) => onChange({ dateTo: date });

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs} adapterLocale="th">
      <Grid container spacing={2}>
        {canSelectBranch && (
          <Grid size={{ xs: 12, sm: 6, md: 3 }}>
            <TextField
              select
              size="small"
              fullWidth
              label="สาขา"
              value={value.branchCode}
              onChange={handleBranchChange}
            >
              <MenuItem value={ALL}>ทั้งหมด</MenuItem>
              {branches.map((branch) => (
                <MenuItem key={branch.uuid} value={branch.branch_code}>
                  {branch.branch_code} - {branch.branch_name}
                </MenuItem>
              ))}
            </TextField>
          </Grid>
        )}
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            select
            size="small"
            fullWidth
            label="ประเภท"
            value={value.noteType}
            onChange={handleNoteTypeChange}
          >
            {NOTE_TYPE_FILTER.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DatePicker
            label="วันที่เอกสาร ตั้งแต่"
            value={value.dateFrom}
            format="DD/MM/YYYY"
            onChange={handleDateFromChange}
            maxDate={value.dateTo ?? undefined}
            slotProps={{
              textField: { size: "small", fullWidth: true },
              field: { clearable: true },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <DatePicker
            label="ถึง"
            value={value.dateTo}
            format="DD/MM/YYYY"
            onChange={handleDateToChange}
            minDate={value.dateFrom ?? undefined}
            slotProps={{
              textField: { size: "small", fullWidth: true },
              field: { clearable: true },
            }}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            select
            size="small"
            fullWidth
            label="สถานะ"
            value={value.status}
            onChange={handleStatusChange}
          >
            {NOTE_STATUS_FILTER.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            select
            size="small"
            fullWidth
            label="การชำระเงิน"
            value={value.settlementMethod}
            onChange={handleSettlementChange}
          >
            {NOTE_SETTLEMENT_FILTER.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <TextField
            select
            size="small"
            fullWidth
            label="ประเภทเหตุผล"
            value={value.reasonType}
            onChange={handleReasonTypeChange}
          >
            {NOTE_REASON_TYPE_FILTER.map((option) => (
              <MenuItem key={option.value} value={option.value}>
                {option.label}
              </MenuItem>
            ))}
          </TextField>
        </Grid>
        <Grid size={{ xs: 12, md: canSelectBranch ? 3 : 6 }}>
          <TextField
            size="small"
            fullWidth
            label="ค้นหา"
            placeholder="เลขที่เอกสาร / เลขที่ใบกำกับภาษีเดิม / ลูกค้า"
            value={value.search}
            onChange={handleSearchChange}
            slotProps={searchSlotProps}
          />
        </Grid>
      </Grid>
    </LocalizationProvider>
  );
}
